import { motion } from "framer-motion";

export function ProblemIllustration() {
  return (
    <svg viewBox="0 0 360 300" fill="none" className="w-full h-full">
      {/* Overlapping chaotic zones */}
      <motion.path
        d="M 60 80 L 180 60 L 220 140 L 100 170 Z"
        fill="hsl(0 100% 70% / 0.08)"
        stroke="hsl(0 100% 70% / 0.25)"
        strokeWidth={1.5}
        strokeDasharray="6 4"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
      />
      <motion.path
        d="M 140 50 L 280 40 L 310 130 L 170 150 Z"
        fill="hsl(43 90% 61% / 0.1)"
        stroke="hsl(43 90% 61% / 0.3)"
        strokeWidth={1.5}
        strokeDasharray="6 4"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
      />
      <motion.path
        d="M 80 140 L 200 120 L 240 220 L 110 240 Z"
        fill="hsl(186 100% 18.4% / 0.08)"
        stroke="hsl(186 100% 18.4% / 0.2)"
        strokeWidth={1.5}
        strokeDasharray="6 4"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.6 }}
      />
      <motion.path
        d="M 180 130 L 320 110 L 340 230 L 200 240 Z"
        fill="hsl(0 100% 70% / 0.06)"
        stroke="hsl(0 100% 70% / 0.15)"
        strokeWidth={1.5}
        strokeDasharray="6 4"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.8 }}
      />

      {/* Overlap conflict zone - highlighted */}
      <motion.path
        d="M 170 120 L 200 115 L 210 145 L 180 150 Z"
        fill="hsl(0 100% 70% / 0.2)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 1.0 }}
      />

      {/* Wrong address labels with X marks */}
      {[
        { x: 120, y: 100, label: "Gatan 4?", delay: 1.0 },
        { x: 250, y: 85, label: "Väg 12??", delay: 1.2 },
        { x: 160, y: 195, label: "Felaktig", delay: 1.4 },
      ].map((addr, i) => (
        <motion.g
          key={`addr-${i}`}
          initial={{ opacity: 0, y: -5 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: addr.delay }}
        >
          {/* Label background */}
          <rect
            x={addr.x - 30}
            y={addr.y - 10}
            width={60}
            height={20}
            rx={4}
            fill="hsl(0 0% 100%)"
            stroke="hsl(0 100% 70% / 0.4)"
            strokeWidth={1}
          />
          <text
            x={addr.x}
            y={addr.y + 4}
            textAnchor="middle"
            fontSize="9"
            fontWeight="500"
            fill="hsl(0 100% 50%)"
          >
            {addr.label}
          </text>
          {/* X mark */}
          <motion.g
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{
              type: "spring",
              stiffness: 300,
              delay: addr.delay + 0.2,
            }}
          >
            <circle cx={addr.x + 35} cy={addr.y} r={8} fill="hsl(0 84% 60%)" />
            <path
              d={`M ${addr.x + 31} ${addr.y - 4} L ${addr.x + 39} ${addr.y + 4}`}
              stroke="hsl(0 0% 100%)"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
            <path
              d={`M ${addr.x + 39} ${addr.y - 4} L ${addr.x + 31} ${addr.y + 4}`}
              stroke="hsl(0 0% 100%)"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          </motion.g>
        </motion.g>
      ))}

      {/* Stray dashed lines going nowhere */}
      {[
        { d: "M 90 250 C 130 260, 150 230, 160 270", delay: 1.3 },
        { d: "M 270 180 C 300 160, 310 200, 330 170", delay: 1.5 },
        { d: "M 50 190 C 70 170, 40 150, 65 130", delay: 1.7 },
      ].map((line, i) => (
        <motion.path
          key={`stray-${i}`}
          d={line.d}
          stroke="hsl(190 8% 45% / 0.3)"
          strokeWidth={1}
          strokeDasharray="3 3"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 0.6 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: line.delay }}
        />
      ))}

      {/* Question marks */}
      {[
        { x: 290, y: 260, delay: 1.6 },
        { x: 60, y: 55, delay: 1.8 },
      ].map((qm, i) => (
        <motion.text
          key={`qm-${i}`}
          x={qm.x}
          y={qm.y}
          textAnchor="middle"
          fontSize="22"
          fontWeight="700"
          fill="hsl(0 100% 70% / 0.35)"
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{
            type: "spring",
            stiffness: 200,
            delay: qm.delay,
          }}
        >
          ?
        </motion.text>
      ))}

      {/* Warning triangle */}
      <motion.g
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ type: "spring", stiffness: 250, delay: 2.0 }}
      >
        <path
          d="M 200 260 L 185 285 L 215 285 Z"
          fill="hsl(43 90% 61%)"
          stroke="hsl(43 90% 61% / 0.8)"
          strokeWidth={1}
        />
        <text x={200} y={281} textAnchor="middle" fontSize="12" fontWeight="700" fill="hsl(190 15% 15%)">
          !
        </text>
      </motion.g>
    </svg>
  );
}
